/**
 * StintSense — API Client
 * Typed fetch wrapper with auth cookie support, 401 auto-refresh, and error handling.
 */

const BASE_URL = "";

// #794: a 401 refreshes the access cookie and retries, for every call EXCEPT the ones
// where that is wrong: the sign-in, registration, refresh and logout flows (no loops),
// the password flows (a change-password 401 means the CURRENT password was wrong, and
// a retry would spend a second attempt against the #479 limit), and /me, the session
// probe. A denylist, so a new signed-in /api/auth/ call refreshes by default.
const NO_REFRESH_EXACT = new Set([
  "/api/auth/login", "/api/auth/login-simple", "/api/auth/register", "/api/auth/register-team",
  "/api/auth/register-individual", "/api/auth/refresh", "/api/auth/logout",
  "/api/auth/forgot-password", "/api/auth/reset-password", "/api/auth/change-password", "/api/auth/me",
  "/api/auth/session",
  // #398: a 401 here means the password was wrong; a retry would spend a second attempt.
  "/api/auth/me/delete", "/api/auth/restore",
]);
const NO_REFRESH_PREFIX = ["/api/auth/iracing/", "/api/auth/desktop/"];

export function shouldRefreshOn401(path: string): boolean {
  const bare = path.split("?")[0];
  return !NO_REFRESH_EXACT.has(bare) && !NO_REFRESH_PREFIX.some((p) => bare.startsWith(p));
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** The parsed JSON body, when there was one: a caller that branches on a machine
     *  field (`code`, `teams`, …) reads it here, never by matching the message (#398). */
    public body: unknown = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** The readable message of an error body. An object `detail` gives its `message` (or
 *  the HTTP status), never "[object Object]"; a validation list is joined. */
export function errorMessage(parsed: unknown, status: number, raw: string): string {
  const fallback = raw || `HTTP ${status}`;
  if (!parsed || typeof parsed !== "object") return fallback;
  const detail = (parsed as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // FastAPI validation errors are a list of {loc,msg,...}; format readably.
    return detail.map((e: { msg?: string }) => e?.msg || JSON.stringify(e)).join("; ");
  }
  if (detail && typeof detail === "object") {
    const m = (detail as { message?: unknown }).message;
    return typeof m === "string" ? m : `HTTP ${status}`;
  }
  return fallback;
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

let _refreshing: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  if (_refreshing) return _refreshing;
  const task = (async () => {
    try {
      const r = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      return r.ok;
    } catch {
      return false;
    }
  })();
  _refreshing = task;
  try {
    return await task;
  } finally {
    // Always clear on settle so the next 401 triggers a fresh refresh.
    _refreshing = null;
  }
}

function xhrToApiError(xhr: XMLHttpRequest): ApiError {
  const parsed = parseJson(xhr.responseText);
  return new ApiError(xhr.status || 0, errorMessage(parsed, xhr.status, xhr.responseText), parsed);
}

export async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${BASE_URL}${path}`;
  // Destructure headers out first: spreading ...options AFTER the merged headers
  // would clobber Content-Type when a caller passes any headers of its own.
  const { headers: callerHeaders, ...rest } = options ?? {};
  const fetchOpts: RequestInit = {
    credentials: "include",
    ...rest,
    headers: {
      // #1234: only with a body; a bodyless GET with it doesn't match index.html's preload in Firefox
      ...(rest.body != null ? { "Content-Type": "application/json" } : {}),
      ...callerHeaders,
    },
  };

  let res = await fetch(url, fetchOpts);

  if (res.status === 401 && shouldRefreshOn401(path)) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      res = await fetch(url, fetchOpts);
    }
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    const parsed = parseJson(body);
    throw new ApiError(res.status, errorMessage(parsed, res.status, body), parsed);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

/**
 * Authenticated file download: GET a binary endpoint with the auth cookie,
 * retry once on 401-refresh, then save the response to disk using the server's
 * Content-Disposition filename (falling back to `fallbackName`).
 */
export async function downloadFile(path: string, fallbackName: string): Promise<string> {
  const url = `${BASE_URL}${path}`;
  const opts: RequestInit = { credentials: "include" };

  let res = await fetch(url, opts);
  if (res.status === 401 && shouldRefreshOn401(path)) {
    const refreshed = await tryRefreshToken();
    if (refreshed) res = await fetch(url, opts);
  }
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let parsed: unknown = null;
    try { parsed = JSON.parse(body); } catch { /* not JSON */ }
    // The shared parser: a detail object or list never becomes "[object Object]" (#919).
    throw new ApiError(res.status, errorMessage(parsed, res.status, body), parsed);
  }

  // Prefer the server-supplied filename (already sanitized server-side).
  let filename = fallbackName;
  const disp = res.headers.get("content-disposition");
  const match = disp && /filename="?([^"]+)"?/i.exec(disp);
  if (match && match[1]) filename = match[1];

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoked later, not right after click(): some browsers cancel a download whose
    // URL is revoked at once (#919 council).
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  }
  return filename;
}

export async function uploadFile(
  path: string,
  file: File,
  extraFields?: Record<string, string>,
  onProgress?: (pct: number) => void
): Promise<{ jobId: string; status: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE_URL}${path}`);
    xhr.withCredentials = true;

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    const safeParse = (text: string): unknown => {
      try {
        return JSON.parse(text);
      } catch {
        return null;
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const parsed = safeParse(xhr.responseText);
        if (parsed === null) {
          return reject(new ApiError(xhr.status, "Malformed server response"));
        }
        return resolve(parsed as { jobId: string; status: string });
      }
      if (xhr.status === 401) {
        tryRefreshToken().then((ok) => {
          if (!ok) return reject(new ApiError(401, "Unauthorized"));
          const retry = new XMLHttpRequest();
          retry.open("POST", `${BASE_URL}${path}`);
          retry.withCredentials = true;
          const retryForm = new FormData();
          retryForm.append("file", file);
          if (extraFields) {
            Object.entries(extraFields).forEach(([k, v]) => retryForm.append(k, v));
          }
          retry.onload = () => {
            if (retry.status >= 200 && retry.status < 300) {
              const parsed = safeParse(retry.responseText);
              if (parsed === null) {
                return reject(new ApiError(retry.status, "Malformed server response"));
              }
              return resolve(parsed as { jobId: string; status: string });
            }
            reject(xhrToApiError(retry));
          };
          retry.onerror = () => reject(new ApiError(0, "Upload failed"));
          retry.send(retryForm);
        });
        return;
      }
      reject(xhrToApiError(xhr));
    };

    xhr.onerror = () => reject(new ApiError(0, "Upload failed"));

    const form = new FormData();
    form.append("file", file);
    if (extraFields) {
      Object.entries(extraFields).forEach(([k, v]) => form.append(k, v));
    }
    xhr.send(form);
  });
}
